import { type RefObject, useLayoutEffect } from "react";

/** Follow the painted shell, including its asymmetric voice and update states. */
export function useCompactResourceOffset(
  clusterRef: RefObject<HTMLDivElement | null>,
) {
  useLayoutEffect(() => {
    const cluster = clusterRef.current;
    const shell = cluster?.querySelector<HTMLElement>(
      ".denker-compact-denker-shell",
    );
    if (!cluster || !shell) return;

    const measure = () => {
      const anchor = cluster.getBoundingClientRect();
      const bounds = shell.getBoundingClientRect();
      cluster.style.setProperty(
        "--compact-resource-left-offset",
        `${Math.max(0, anchor.left - bounds.left)}px`,
      );
      cluster.style.setProperty(
        "--compact-resource-right-offset",
        `${Math.max(0, bounds.right - anchor.right)}px`,
      );
    };

    measure();
    // Width transitions emit resize observations on each animation frame.
    // State mutations and transition completion also cover transform-only changes.
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    resizeObserver?.observe(shell);
    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(shell, { attributes: true });
    shell.addEventListener("transitionend", measure);
    window.addEventListener("resize", measure);
    return () => {
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
      shell.removeEventListener("transitionend", measure);
      window.removeEventListener("resize", measure);
    };
  }, [clusterRef]);
}
